/**
 * "A sessao do Access expirou", como estado observavel pela interface.
 *
 * ## O defeito que isto conserta
 *
 * Atras do Cloudflare Access, uma sessao expirada NAO responde 401: responde
 * **302 para o login**, noutro dominio. Um `fetch` com `redirect: 'follow'`
 * segue esse desvio, esbarra no CORS e **rejeita com TypeError** — exatamente
 * a mesma forma de uma queda de rede.
 *
 * O cliente tratava as duas coisas como uma: `ehFalhaDeRede` devolvia `true`,
 * a escrita ia para a fila e a leitura caia no cache do service worker. O
 * resultado, observado num Android com a PWA instalada: **lista desatualizada,
 * duas escritas presas e nenhuma explicacao** — o app parecia offline com o
 * aparelho perfeitamente conectado.
 *
 * Pior: a checagem do proprio `sw.js` sofre o mesmo desvio, entao o aparelho
 * tambem **para de receber versoes novas**. Um app que nao consegue nem dizer
 * "entre de novo" nem se atualizar sai do alcance de qualquer correcao.
 *
 * A fila NAO e descartada quando isto acontece: a escrita continua valida e
 * drena sozinha assim que a sessao volta. O que muda e a tela parar de mentir.
 */

type Ouvinte = () => void;

const ouvintes = new Set<Ouvinte>();
let expirada = false;

export function sessaoExpirada(): boolean {
  return expirada;
}

export function anunciarSessaoExpirada(): void {
  if (expirada) return;
  expirada = true;
  for (const ouvinte of ouvintes) ouvinte();
}

/** Chamado quando uma requisicao volta a funcionar. */
export function anunciarSessaoValida(): void {
  if (!expirada) return;
  expirada = false;
  for (const ouvinte of ouvintes) ouvinte();
}

export function assinarSessao(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

/**
 * Uma resposta que e, na verdade, o desvio do Access para a tela de login.
 *
 * Com `redirect: 'manual'` o navegador nao segue o 302: entrega uma resposta
 * **opaca** (`type: 'opaqueredirect'`, `status: 0`). E o unico sinal
 * disponivel — o corpo e os cabecalhos do desvio sao invisiveis para o script,
 * por desenho do CORS.
 *
 * A API deste app nunca responde 3xx por conta propria, entao um desvio aqui so
 * pode ter vindo da camada de autenticacao.
 */
export function ehDesvioDeLogin(res: { type?: string; status: number }): boolean {
  return res.type === 'opaqueredirect' || res.status === 0;
}
